import { FormGroup, Grid, List, ListItem, Skeleton } from "@mui/material"
import { AccountOrder, OrderCancellationRequest } from "../../apis/ersys-data/api"
import { useEffect, useState } from "react"
import { useErsysDataApi } from "../ErsysDataApiProvider"
import { showError } from "../../features/errors/Errors"
import { setIsLoading } from "../../features/menus/AppMenu"
import { useDispatch } from "react-redux"
import { useParams } from "react-router-dom"
import AccountOrderComponent from "./AccountOrderComponent"
import OrderCancellationModal from "./modals/OrderCancellationModal"

interface AccountOrdersTabProps {
    formSpacing: number
}

const AccountOrdersTab: React.FC<AccountOrdersTabProps> = () => {
    const ersysDataApiServices = useErsysDataApi()
    const [accountOrders, setAccountOrders] = useState<AccountOrder[]>([])
    const [selectedAccountOrder, setSelectedAccountOrder] = useState<AccountOrder | null>(null)
    const [isCancelVisible, setIsCancelVisible] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [loaded, setLoaded] = useState<boolean>(false)
    const dispatch = useDispatch()
    const params = useParams()

    useEffect(() => {
        dispatch(setIsLoading(loading))
    }, [loading, dispatch])

    useEffect(() => {
        dispatch(setIsLoading(true))
        const fetch = async () => {
            try {
                var resp = await ersysDataApiServices.accountsService.accountsListOrders(parseInt(params.id!, 10))
                setAccountOrders(resp.data.orders)
            }
            catch (ex: any) {
                dispatch(
                    showError({
                        title: "Unexpected Error occurred",
                        message:
                            "An unexpected error occurred while getting order data. If problem persists please contact IT.",
                    }),
                )
                console.log(ex)
            } finally {
                setLoaded(true)
                dispatch(setIsLoading(false))
            }
        }
        fetch()
    }, [dispatch, setAccountOrders, ersysDataApiServices.accountsService, params.id])

    
    const showSkeleton = () => {
        return accountOrders.length < 1 && !loaded
    }

    const renderSkeleton = () => {
        return (
            <FormGroup>
                {[...Array(5)].map((_, i) => (
                    <Skeleton variant='rectangular' width={600} height={150} key={`skele-${i}`} sx={{margin: 1}}/>
                ))}
            </FormGroup>
        )
    }

    const onCancelOrder = async (req: OrderCancellationRequest) => {
        setIsCancelVisible(false)
        setLoading(true)
        try {
            if(!selectedAccountOrder) {
                return
            }
            const accountId = parseInt(params.id!, 10)
            const refundValue = parseInt((req.refundValue ?? 0).toString(), 10)
            const refundTaxValue = parseInt((req.taxRefundValue ?? 0).toString(), 10)
            await ersysDataApiServices.accountsService.accountsCancelOrder(accountId, { orderId: selectedAccountOrder.id, refundValue: refundValue, taxRefundValue: refundTaxValue})
            var resp = await ersysDataApiServices.accountsService.accountsListOrders(accountId)
            setAccountOrders(resp.data.orders)
        }
        catch (ex: any) {
            dispatch(
                showError({
                    title: "Unexpected Error occurred",
                    message:
                        "An unexpected error occurred while cancelling order. If problem persists please contact IT.",
                }),
            )
            console.log(ex)
        } finally {
            setLoaded(true)
            setLoading(false)
        }
    } 

    return (
        <Grid container alignItems="center" justifyContent="center">
            <OrderCancellationModal
                onClose={() => setIsCancelVisible(false)}
                onCreate={onCancelOrder}
                open={isCancelVisible}
                />
            {showSkeleton() ? renderSkeleton() :
                <List sx={{height: 550, overflow: 'auto'}}>
                     {accountOrders.map((order) => (
                        <ListItem key={order.id}>
                            <AccountOrderComponent order={order} onCancel={() => {
                                setSelectedAccountOrder(order)
                                setIsCancelVisible(true)
                                }
                                }/>
                        </ListItem>
                     ))}
                </List>
            }
        </Grid>
    )
}

export default AccountOrdersTab